import { useBoxService } from "../hooks/use-box-service";
import { useUiService } from "../hooks/use-ui-service";
import classnames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { ConstantsService } from "../services/constants-service";
import { ColorType2 } from "../model/color-type2";
import React from "react";

const box = "Box";
const back = "Back";
const door = "Door";
const shelf = "Shelf";
const drawer = "Drawer";
const corner = "Corner";
const divider = "Divider";
const small = "small";
const tall = "tall";
const wide = "wide";
const large = "large";
const smallCorner = "small-corner";
const tallCorner = "tall-corner";
const tallWide = "tall/wide";
const smallTall = "small/tall";
const wideLarge = "wide/large";

interface ShelvesItemState {
  readonly product: string;
  readonly size: string;
  readonly quantity: number;
  readonly price: number;
}

interface ShelvesState {
  readonly color: string;
  readonly items: ShelvesItemState[];
  readonly total: number;
  readonly discountCode: string | undefined;
  readonly disclaimer: string | undefined;
  readonly shipping: string | undefined;
  readonly checkoutUrl: string | undefined;
}

export const Cart = () => {
  const boxService = useBoxService();
  const { uiService, uiState } = useUiService();

  const [shelves, setShelves] = useState<ShelvesState>({
    color: "Walnut",
    items: [],
    total: 0,
    discountCode: undefined,
    disclaimer: undefined,
    shipping: undefined,
    checkoutUrl: undefined,
  });

  const onClose = useCallback(() => {
    uiService.hideCart();
  }, [uiService]);

  useEffect(() => {
    const existingQueryString = window.location.search;

    const urlParams = new URLSearchParams(existingQueryString);

    if (urlParams.has("discount")) {
      setShelves({
        ...shelves,
        discountCode: urlParams.get("discount"),
        disclaimer:
          "Kickstarter codes will be automatically added in the next step",
      });
    }
  }, [window.location.search]);

  useEffect(() => {
    function generateTableDataByProduct(
      product: string,
      finalCount,
    ): ShelvesItemState[] {
      const items: ShelvesItemState[] = [];

      switch (product) {
        case box:
          if (finalCount[small].box.count !== 0) {
            items.push(addTableData(finalCount, small, product));
          }
          if (finalCount[tall].box.count !== 0) {
            items.push(addTableData(finalCount, tall, product));
          }
          if (finalCount[wide].box.count !== 0) {
            items.push(addTableData(finalCount, wide, product));
          }
          if (finalCount[large].box.count !== 0) {
            items.push(addTableData(finalCount, large, product));
          }

          break;
        case back:
          if (finalCount[small].back.count !== 0) {
            items.push(addTableData(finalCount, small, product));
          }
          if (
            finalCount[tall].back.count !== 0 ||
            finalCount[wide].back.count !== 0
          ) {
            items.push(addTableData(finalCount, tallWide, product));
          }
          if (finalCount[large].back.count !== 0) {
            items.push(addTableData(finalCount, large, product));
          }

          break;
        case door:
          if (finalCount[small].door.count !== 0) {
            if (finalCount[small].door.count % 2 === 1) {
              finalCount[small].door.count += 1;
            }

            items.push(addTableData(finalCount, small, product));
          }

          if (
            finalCount[tall].door.count !== 0 ||
            finalCount[wide].door.count !== 0
          ) {
            if (
              (finalCount[tall].door.count + finalCount[wide].door.count) %
                2 ===
              1
            ) {
              finalCount[tall].door.count += 1;
            }

            items.push(addTableData(finalCount, tallWide, product));
          }

          if (finalCount[large].door.count !== 0) {
            if (finalCount[large].door.count % 2 === 1) {
              finalCount[large].door.count += 1;
            }

            items.push(addTableData(finalCount, large, product));
          }

          break;
        case shelf:
          if (
            finalCount[small].shelf.count !== 0 ||
            finalCount[tall].shelf.count !== 0
          ) {
            if (
              (finalCount[small].shelf.count + finalCount[tall].shelf.count) %
                2 ===
              1
            ) {
              finalCount[small].shelf.count += 1;
            }

            items.push(addTableData(finalCount, smallTall, product));
          }

          if (
            finalCount[wide].shelf.count !== 0 ||
            finalCount[large].shelf.count !== 0
          ) {
            if (
              (finalCount[wide].shelf.count + finalCount[large].shelf.count) %
                2 ===
              1
            ) {
              finalCount[wide].shelf.count += 1;
            }

            items.push(addTableData(finalCount, wideLarge, product));
          }

          break;
        case drawer:
          if (
            finalCount[small].drawer.count !== 0 ||
            finalCount[tall].drawer.count !== 0
          ) {
            if (
              (finalCount[small].drawer.count + finalCount[tall].drawer.count) %
                2 ===
              1
            ) {
              finalCount[small].drawer.count += 1;
            }

            items.push(addTableData(finalCount, smallTall, product));
          }
          if (
            finalCount[wide].drawer.count !== 0 ||
            finalCount[large].drawer.count !== 0
          ) {
            if (
              (finalCount[wide].drawer.count + finalCount[large].drawer.count) %
                2 ===
              1
            ) {
              finalCount[wide].drawer.count += 1;
            }

            items.push(addTableData(finalCount, wideLarge, product));
          }

          break;
        case divider:
          if (finalCount[small].divider.count !== 0) {
            items.push(addTableData(finalCount, small, product));
          }
          if (
            finalCount[tall].divider.count !== 0 ||
            finalCount[wide].divider.count !== 0
          ) {
            items.push(addTableData(finalCount, tallWide, product));
          }
          if (finalCount[large].divider.count !== 0) {
            items.push(addTableData(finalCount, large, product));
          }

          break;
        case corner:
          if (
            finalCount[smallCorner].right.count !== 0 ||
            finalCount[smallCorner].left.count !== 0
          ) {
            items.push(addTableData(finalCount, smallCorner, product));
          }

          if (
            finalCount[tallCorner].right.count !== 0 ||
            finalCount[tallCorner].left.count !== 0
          ) {
            items.push(addTableData(finalCount, tallCorner, product));
          }

          break;
      }

      return items;
    }

    function addTableData(finalCount, key, product): ShelvesItemState {
      const basePrices = ConstantsService.prices;

      let quantity = 0;
      let price = 0;

      switch (product) {
        case box:
          quantity = finalCount[key].box.count;

          price = basePrices[key].price;

          break;
        case back:
          if (key === tallWide || key === smallTall || key === wideLarge) {
            const { firstKey, secondKey } = getIndividualKeys(key);

            quantity =
              finalCount[firstKey].back.count +
              finalCount[secondKey].back.count;

            price = basePrices[firstKey].backPrice;
          } else {
            quantity = finalCount[key].back.count;

            price = basePrices[key].backPrice;
          }

          break;
        case door:
          if (key === tallWide || key === smallTall || key === wideLarge) {
            const { firstKey, secondKey } = getIndividualKeys(key);

            quantity =
              finalCount[firstKey].door.count +
              finalCount[secondKey].door.count;

            price = basePrices[firstKey].doorPrice;
          } else {
            quantity = finalCount[key].door.count;

            price = basePrices[key].doorPrice;
          }

          break;
        case divider:
          if (key === tallWide || key === smallTall || key === wideLarge) {
            const { firstKey, secondKey } = getIndividualKeys(key);

            quantity =
              finalCount[firstKey].divider.count +
              finalCount[secondKey].divider.count;

            price = basePrices[firstKey].crossDivider;
          } else {
            quantity = finalCount[key].divider.count;

            price = basePrices[key].crossDivider;
          }

          break;
        case shelf:
          if (key === tallWide || key === smallTall || key === wideLarge) {
            const { firstKey, secondKey } = getIndividualKeys(key);

            quantity =
              finalCount[firstKey].shelf.count +
              finalCount[secondKey].shelf.count;

            price = basePrices[firstKey].shelfPrice;
          } else {
            quantity = finalCount[key].shelf.count;

            price = basePrices[key].shelfPrice;
          }

          break;
        case drawer:
          if (key === tallWide || key === smallTall || key === wideLarge) {
            const { firstKey, secondKey } = getIndividualKeys(key);

            quantity =
              finalCount[firstKey].drawer.count +
              finalCount[secondKey].drawer.count;

            price = basePrices[firstKey].drawerPrice;
          } else {
            quantity = finalCount[key].drawer.count;

            price = basePrices[key].drawerPrice;
          }

          break;
        case corner:
          quantity = finalCount[key].right.count + finalCount[key].left.count;

          price = basePrices[key].price;

          break;
      }

      const capitalizedKey = capitalizeKey(key);

      return {
        product,
        quantity,
        size: capitalizedKey,
        price,
      };
    }

    function capitalizeKey(key: string) {
      const splitIndex = key.indexOf("/");

      if (splitIndex > -1) {
        const { firstKey, secondKey } = getIndividualKeys(key);

        const firstCapitalizedKey =
          firstKey.charAt(0).toUpperCase() + firstKey.slice(1);

        const secondCapitalizedKey =
          secondKey.charAt(0).toUpperCase() + secondKey.slice(1);

        return `${firstCapitalizedKey}/${secondCapitalizedKey}`;
      }

      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

      return capitalizedKey;
    }

    function getIndividualKeys(key: string) {
      const splitIndex = key.indexOf("/");

      const firstKey = key.substring(0, splitIndex);

      const secondKey = key.substring(splitIndex + 1, key.length);

      return {
        firstKey: firstKey,
        secondKey: secondKey,
      };
    }

    if (uiState.cartVisible && boxService.getKonvoBoxes().length > 0) {
      const finalCount = boxService.getComponentsCount();

      const products = [box, back, door, shelf, drawer, divider, corner];
      const items: ShelvesItemState[] = [];

      for (const product of products) {
        items.push(...generateTableDataByProduct(product, finalCount));
      }

      const total = boxService.getCartCost();

      const finalCart = boxService.getCartCount();

      const cartItems = finalCart.filter(function (item) {
        return item.variantId !== 0 && item.count !== 0;
      });
      const canCheckout = cartItems.length !== 0;

      let checkoutUrl: string | undefined = undefined;
      if (canCheckout) {
        const queryString = cartItems
          .map(function (item) {
            return `${item.variantId}:${item.count}`;
          })
          .join(",");

        let link = `https://boardgametables.myshopify.com/cart/${queryString}`;

        if (shelves.discountCode) {
          link += "?discount=" + shelves.discountCode;
        }

        checkoutUrl = link;
      }

      const newShelves: ShelvesState = {
        ...shelves,
        color: boxService.getColor2().is(ColorType2.walnut)
          ? "Walnut"
          : "Natural",
        items,
        total,
        shipping:
          "US orders shipping within 1 week. Canada orders shipping within 3-4 weeks.",
        checkoutUrl,
      };

      setShelves(newShelves);
    } else {
      const newShelves: ShelvesState = {
        ...shelves,
        color: boxService.getColor2().is(ColorType2.walnut)
          ? "Walnut"
          : "Natural",
        items: [],
        total: 0,
        shipping:
          "US orders shipping within 1 week. Canada orders shipping within 3-4 weeks.",
        checkoutUrl: undefined,
      };

      setShelves(newShelves);
    }
  }, [boxService, uiState.cartVisible]);

  return (
    <div
      id="app-cart-popup"
      className={classnames({
        "wizard-modal fixed bottom-0 inset-x-0 pb-6 sm:inset-0 sm:p-0 sm:items-center sm:justify-center z-40": true,
        hidden: !uiState.cartVisible,
      })}
    >
      <div className="justify-center items-center flex overflow-x-auto overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="wizard-modal--step-1 w-96 bg-white rounded-md sm:justify-start flex-col relative sm:flex">
          <div className="overflow-y-auto h-full shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="table table-fixed w-full">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider text-right">
                    Qty
                  </th>
                  <th className="px-4 py-3 text-left text- sm font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider text-right">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 overflow-y-auto block max-h-custom">
                {shelves.items.map((i) => (
                  <tr className={"table table-fixed w-full"}>
                    <td
                      className={
                        "px-4 py-2 whitespace-nowrap text-md text-gray-500 text-right"
                      }
                    >
                      {i.quantity}
                    </td>
                    <td
                      className={
                        "px-4 py-2 whitespace-nowrap text-md font-medium text-gray-900"
                      }
                    >
                      {i.product}
                    </td>
                    <td
                      className={
                        "px-4 py-2 whitespace-nowrap text-md text-gray-500"
                      }
                    >
                      {i.size}
                    </td>
                    <td
                      className={
                        "px-4 py-2 whitespace-nowrap text-md text-gray-500 text-right"
                      }
                    >
                      {i.price.toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="py-2 px-4 bg-gray-50 border-b">
            <p className="inline-flex items-center text-md">
              Color: {shelves.color}
            </p>
            <p className="inline-flex items-center text-md float-right">
              Total ${shelves.total.toFixed(0)}
            </p>
          </div>
          <div className="py-2 px-4 bg-gray-50 border-b">
            <p className="inline-flex items-center text-md">
              {shelves.shipping}
            </p>
          </div>
          <div className="py-2 mt-2 px-4">
            <div>
              <p
                className={classnames({
                  "font-bold text-center": true,
                  "pb-2": shelves.discountCode,
                })}
              >
                {shelves.discountCode}
              </p>
              <p
                className={classnames({
                  "italic text-center": true,
                  "pb-2": shelves.disclaimer,
                })}
              >
                {shelves.disclaimer}
              </p>
            </div>
            <div>
              <button
                className="h-10 px-5 text-blue-700 transition-colors duration-150 border border-blue-500 rounded-lg focus:shadow-outline hover:bg-blue-500 hover:text-blue-100"
                onClick={onClose}
              >
                Close
              </button>
              <a
                className={classnames({
                  "h-10 px-5 text-blue-700 transition-colors duration-150 border border-blue-500 rounded-lg focus:shadow-outline hover:bg-blue-500 hover:text-blue-100 float-right flex items-center": true,
                  hidden: !shelves.checkoutUrl,
                })}
                href={shelves.checkoutUrl}
                target="_blank"
                rel="noreferrer"
              >
                Checkout
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 bg-gray-500 opacity-50"></div>
    </div>
  );
};
